/**
 * chrome.tsx — persistent instrument chrome: the boundary-ladder ribbon (with the
 * language switcher and AI-model picker), the cognitive-load HUD, and the streaming
 * agent console.
 */
import { useEffect, useRef, useState } from "react";
import { BOUNDARY_LADDER } from "../../domain/pipeline.ts";
import { streamAgent, cancelAgent, bumpData, type ChatMessage } from "../lib/api.ts";
import { onConsoleRequest } from "../lib/console-bus.ts";
import type { LoadLevel } from "../../domain/types.ts";
import { navigate, useRoute } from "../lib/router.tsx";
import { AutoTextarea } from "../lib/auto-textarea.tsx";
import { useI18n, useT, LocaleSwitch } from "../lib/i18n.tsx";
import { ThemeSwitch } from "../lib/theme.tsx";

interface ModelInfo {
  current: string;
  synthetic: boolean;
  suggested: { id: string; label: string; note?: string; tier?: string }[];
}

function ModelPicker(): JSX.Element | null {
  const t = useT();
  const [info, setInfo] = useState<ModelInfo | null>(null);
  useEffect(() => {
    fetch("/api/models")
      .then((r) => r.json())
      .then((d: ModelInfo) => setInfo(d))
      .catch(() => undefined);
  }, []);
  if (!info) return null;
  const onPick = (model: string): void => {
    setInfo((prev) => (prev ? { ...prev, current: model } : prev));
    void fetch("/api/model", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ model }) });
  };
  // The synthetic provider's pseudo-id (lineage/synthetic-v1) is NOT a selectable
  // model — the ribbon already flags synthetic mode — so default the picker to the
  // first suggestion. A real env-override id that isn't in the menu is still shown.
  const fallback = info.suggested[0]?.id ?? info.current;
  const selected = info.synthetic ? fallback : info.current;
  const extra: ModelInfo["suggested"] = !info.synthetic && !info.suggested.some((m) => m.id === info.current)
    ? [{ id: info.current, label: info.current, tier: "Custom" }]
    : [];
  const all = [...extra, ...info.suggested];
  // Stable tier order; anything untiered falls to the end.
  const order = ["Custom", "Frontier", "Balanced", "Fast & cheap"];
  const tiers = [...new Set(all.map((m) => m.tier ?? "Other"))].sort(
    (a, b) => (order.indexOf(a) < 0 ? 99 : order.indexOf(a)) - (order.indexOf(b) < 0 ? 99 : order.indexOf(b)),
  );
  return (
    <label className="model-picker" title={t("common.model")}>
      <span className="gly" aria-hidden="true">✦</span>
      <select value={selected} onChange={(e) => onPick(e.target.value)} aria-label={t("common.model")}>
        {tiers.map((tier) => (
          <optgroup key={tier} label={tier}>
            {all.filter((m) => (m.tier ?? "Other") === tier).map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
                {m.note ? ` — ${m.note}` : ""}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}

/**
 * Signed-in identity + logout. Renders only when server-side OIDC is enabled AND
 * a session exists (`GET /auth/me` → `{ authenticated, user }`). When auth is
 * disabled the endpoint 404s and this stays invisible; when enabled but
 * unauthenticated the gate has already redirected to Keycloak.
 */
function AuthControl(): JSX.Element | null {
  const [user, setUser] = useState<{ email?: string; name?: string } | null>(null);
  useEffect(() => {
    let alive = true;
    fetch("/auth/me")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { authenticated?: boolean; user?: { email?: string; name?: string } } | null) => {
        if (alive && d?.authenticated && d.user) setUser(d.user);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!user) return null;
  const who = user.name || user.email || "signed in";
  return (
    <span className="auth-control" style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
      <span className="auth-who" title={user.email ?? who} style={{ opacity: 0.7, fontSize: "0.8em" }}>{who}</span>
      <a className="btn ghost sm" href="/auth/logout" title="Sign out">⏻</a>
    </span>
  );
}

export function BoundaryRibbon({ onPalette }: { onPalette: () => void }): JSX.Element {
  const t = useT();
  return (
    <div className="ribbon">
      <div className="ribbon-inner">
        <span className="brand" onClick={() => navigate("/")}>
          {t("brand")}
        </span>
        <div className="chain">
          {BOUNDARY_LADDER.map((_, i) => (
            <span key={i} style={{ display: "contents" }}>
              <span className={`node ${i < BOUNDARY_LADDER.length - 1 ? "live" : "last"}`}>{t(`ladder.${i}`)}</span>
              {i < BOUNDARY_LADDER.length - 1 ? <span className={`sep ${i === BOUNDARY_LADDER.length - 2 ? "last" : ""}`}>≠</span> : null}
            </span>
          ))}
        </div>
        <ModelPicker />
        <ThemeSwitch />
        <LocaleSwitch />
        <AuthControl />
        <button className="btn ghost sm palette-btn" onClick={onPalette} title="⌘K">⌘K</button>
      </div>
    </div>
  );
}

const LOAD_NOTES: Record<LoadLevel, { v: string; fill: string; color: string; note: string; noteDe: string }> = {
  low: { v: "LOW", fill: "33%", color: "var(--phosphor)", note: "Capacity available — recall & reconstruction are safe.", noteDe: "Kapazität verfügbar — Erinnern & Rekonstruktion sind sicher." },
  medium: { v: "MED", fill: "60%", color: "var(--amber)", note: "Recognition before recall when fatigue appears.", noteDe: "Wiedererkennen vor Erinnern, sobald Ermüdung auftritt." },
  high: { v: "HIGH", fill: "92%", color: "var(--vermilion)", note: "Drop to recognition. Defer forced choices & reconstruction.", noteDe: "Auf Wiedererkennen wechseln. Erzwungene Wahl & Rekonstruktion aufschieben." },
};

export function LoadHud(): JSX.Element {
  const { locale } = useI18n();
  const [level, setLevel] = useState<LoadLevel>("medium");
  const cfg = LOAD_NOTES[level];
  return (
    <div className="hud">
      <div className="ht">
        <span>{locale === "de" ? "kognitive last" : "cognitive load"}</span>
        <span className="v">{cfg.v}</span>
      </div>
      <div className="gauge">
        <div className="fill" style={{ width: cfg.fill, background: cfg.color }} />
      </div>
      <div className="hset">
        {(["low", "medium", "high"] as const).map((l) => (
          <button key={l} data-l={l} className={l === level ? "on" : ""} onClick={() => setLevel(l)}>
            {l === "medium" ? "med" : l}
          </button>
        ))}
      </div>
      <div className="hnote">{locale === "de" ? cfg.noteDe : cfg.note}</div>
    </div>
  );
}

interface ToolEntry {
  name: string;
  ok?: boolean;
  summary?: string;
  args?: unknown;
}
interface ChatEntry {
  role: "user" | "assistant";
  content: string;
  tools?: ToolEntry[];
}

/** Update the trailing (assistant) entry in-place. */
function patchLast(entries: ChatEntry[], fn: (a: ChatEntry) => ChatEntry): ChatEntry[] {
  if (entries.length === 0) return entries;
  const next = entries.slice();
  next[next.length - 1] = fn(next[next.length - 1]!);
  return next;
}

export function AgentConsole(): JSX.Element {
  const t = useT();
  const { locale } = useI18n();
  const route = useRoute();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // The id of the in-flight run, so the user can cancel it cooperatively.
  const runIdRef = useRef<string | null>(null);
  const [canceling, setCanceling] = useState(false);
  // Which tool chip is expanded (`${entryIdx}-${toolIdx}`) — disclosure on demand.
  const [openTool, setOpenTool] = useState<string | null>(null);

  // Keep the transcript pinned to the latest content as it streams in.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries, open]);

  // Replay-after-edit: /trace asks the console to open with a past prompt queued for
  // editing. We DON'T auto-send — the user reviews/edits, then runs it themselves.
  useEffect(
    () =>
      onConsoleRequest((p) => {
        setOpen(true);
        setPrompt(p);
      }),
    [],
  );

  const send = async (): Promise<void> => {
    const q = prompt.trim();
    if (!q || busy) return;
    setPrompt("");
    // The payload is the prior transcript plus this turn (exclude empty placeholders).
    const history: ChatMessage[] = entries
      .filter((e) => e.content.trim().length > 0)
      .map((e) => ({ role: e.role, content: e.content }));
    const messages: ChatMessage[] = [...history, { role: "user", content: q }];
    setEntries((prev) => [...prev, { role: "user", content: q }, { role: "assistant", content: "", tools: [] }]);
    setBusy(true);
    setCanceling(false);
    try {
      await streamAgent({ messages, locale, route }, (evt) => {
        switch (evt.type) {
          case "RUN_STARTED":
            runIdRef.current = evt.runId;
            break;
          case "RUN_CANCELED":
            setEntries((prev) => patchLast(prev, (a) => ({ ...a, content: a.content + (a.content ? "\n" : "") + "⊘ stopped" })));
            break;
          case "TEXT_MESSAGE_CONTENT":
            setEntries((prev) => patchLast(prev, (a) => ({ ...a, content: a.content + evt.delta })));
            break;
          case "TOOL_CALL":
            setEntries((prev) => patchLast(prev, (a) => ({ ...a, tools: [...(a.tools ?? []), { name: evt.name, args: evt.args }] })));
            break;
          case "TOOL_RESULT":
            setEntries((prev) =>
              patchLast(prev, (a) => {
                const tools = (a.tools ?? []).slice();
                for (let i = tools.length - 1; i >= 0; i--) {
                  if (tools[i]!.name === evt.name && tools[i]!.ok === undefined) {
                    tools[i] = { ...tools[i]!, ok: evt.ok, summary: evt.summary };
                    break;
                  }
                }
                return { ...a, tools };
              }),
            );
            break;
          case "UI_NAVIGATE":
            navigate(evt.path);
            break;
          case "DATA_CHANGED":
            bumpData();
            break;
          case "RUN_ERROR":
            setEntries((prev) => patchLast(prev, (a) => ({ ...a, content: `${a.content}\n[${evt.message}]` })));
            break;
        }
      });
    } catch {
      setEntries((prev) => patchLast(prev, (a) => ({ ...a, content: `${a.content}\n[agent unavailable]` })));
    } finally {
      setBusy(false);
      setCanceling(false);
      runIdRef.current = null;
    }
  };

  // Cooperative cancel: tells the server to unwind the run at its next turn boundary.
  const cancel = (): void => {
    const id = runIdRef.current;
    if (!id || canceling) return;
    setCanceling(true);
    void cancelAgent(id);
  };

  if (!open) {
    return (
      <button className="btn ghost console-toggle" onClick={() => setOpen(true)} title={t("console.title")}>
        {t("console.ask")}
      </button>
    );
  }
  return (
    <div className="console">
      <div className="console-head">
        <span>{t("console.title")}</span>
        <div className="row" style={{ gap: 4 }}>
          {entries.length > 0 ? (
            <button className="btn ghost sm" onClick={() => setEntries([])} title={t("console.clear")} style={{ padding: "3px 8px" }}>↺</button>
          ) : null}
          <button className="btn ghost sm" onClick={() => setOpen(false)} style={{ padding: "3px 8px" }}>×</button>
        </div>
      </div>
      <div className="console-body" ref={bodyRef}>
        {entries.length === 0 ? (
          <div className="chat-intro">{t("console.intro")}</div>
        ) : (
          entries.map((e, i) => {
            const streaming = busy && i === entries.length - 1 && e.role === "assistant";
            return (
              <div className={`chat-msg ${e.role}`} key={i}>
                {e.role === "user" ? <span className="chat-role">{t("console.you")}</span> : null}
                {e.tools && e.tools.length > 0 ? (
                  <div className="chat-tools">
                    {e.tools.map((tl, j) => {
                      const key = `${i}-${j}`;
                      const hasArgs = Boolean(tl.args && typeof tl.args === "object" && Object.keys(tl.args).length > 0);
                      const isOpen = openTool === key;
                      return (
                        <div key={j}>
                          <button
                            type="button"
                            className={`chat-tool ${tl.ok === false ? "bad" : tl.ok ? "ok" : "run"} ${hasArgs ? "expandable" : ""}`}
                            onClick={() => hasArgs && setOpenTool(isOpen ? null : key)}
                          >
                            <span className="ct-icon">{tl.ok === undefined ? "↻" : tl.ok ? "✓" : "✕"}</span>
                            <span className="ct-name">{tl.name}</span>
                            {tl.summary ? <span className="ct-sum">{tl.summary}</span> : null}
                            {hasArgs ? <span className="ct-caret" aria-hidden="true">{isOpen ? "▾" : "▸"}</span> : null}
                          </button>
                          {isOpen && hasArgs ? <code className="ct-args">{JSON.stringify(tl.args)}</code> : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
                {e.content ? <div className="chat-text">{e.content}{streaming ? " ▍" : ""}</div> : streaming ? <div className="chat-text">▍</div> : null}
              </div>
            );
          })
        )}
      </div>
      <div className="console-foot">
        <AutoTextarea
          className="chat-input"
          rows={1}
          value={prompt}
          placeholder={t("console.placeholder")}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        {busy ? (
          <button className="btn warn sm" onClick={cancel} disabled={canceling} title={t("console.stop")}>
            {canceling ? t("console.stopping") : t("console.stop")}
          </button>
        ) : (
          <button className="btn primary sm" onClick={() => void send()}>{t("console.send")}</button>
        )}
      </div>
    </div>
  );
}
